import React from 'react';

/**
 * TabIcons
 *
 * Hand-rolled inline SVG icons for the bottom tab bar. Native iOS/Android
 * tab bars swap an outline glyph for a filled one on selection (house vs
 * house.fill). Each component here does exactly that: pass `filled` to
 * render the solid/active variant, omit it for the outline/inactive one.
 *
 * We avoid pulling in an icon library — these are minimal hand-written SVGs
 * following the same convention as KeypadIcon/DirectionBadge in
 * CallControls.js / CallLog.js. All icons inherit `currentColor`, so they
 * pick up the parent button's color (the `.tab-btn.active` CSS still does the
 * color shift; the glyph shape now matches it).
 *
 * The Phone tab is labelled "Keypad" and renders the dialer / in-call DTMF
 * keypad, so it uses a keypad-grid icon (matching iOS's dialer-tab grid),
 * not a phone receiver.
 */

function TabSvg({ size = 20, filled = false, children, rotate = 0 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function KeypadIcon({ filled = false, size = 20 }) {
  const dots = [
    [6, 6],
    [12, 6],
    [18, 6],
    [6, 12],
    [12, 12],
    [18, 12],
    [6, 18],
    [12, 18],
    [18, 18],
  ];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {dots.map(([x, y]) => (
        <circle
          key={`${x}-${y}`}
          cx={x}
          cy={y}
          r={filled ? 2.1 : 1.6}
          fill={filled ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="1.8"
        />
      ))}
    </svg>
  );
}

function VoicemailIcon({ filled = false, size = 20 }) {
  return (
    <TabSvg size={size} filled={filled}>
      {/* audio waveform — a recorded voicemail */}
      <path d="M3 15l2-6 2 4 2-8 2 10 2-6 2 3 2-2 2 5" />
    </TabSvg>
  );
}

function RecentsIcon({ filled = false, size = 20 }) {
  // history/recents clock with a counter-clockwise arrow
  return (
    <TabSvg size={size} filled={filled}>
      <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89 3.42-3.42H7a7 7 0 1 1 7 7 6.96 6.96 0 0 1-4.95-2.05l-1.41 1.41A9 9 0 1 0 13 3z" />
      <path d="M13 8v4l3 1.8-1 1.5L11 13V8z" />
    </TabSvg>
  );
}

function ContactsIcon({ filled = false, size = 20 }) {
  return (
    <TabSvg size={size} filled={filled}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </TabSvg>
  );
}

function MessagesIcon({ filled = false, size = 20 }) {
  return (
    <TabSvg size={size} filled={filled}>
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H8l-4 3.2V6a1 1 0 0 1 1-1z" />
      <path d="M5 7l7 5 7-5" />
    </TabSvg>
  );
}

function SettingsIcon({ filled = false, size = 20 }) {
  return (
    <TabSvg size={size} filled={filled}>
      <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.33-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.484.484 0 0 0-.59.22l-1.92 3.32c-.12.21-.07.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.21.36.27.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.01-1.58z" />
      <circle cx="12" cy="12" r="3.6" />
    </TabSvg>
  );
}

/** Gear-icon used for the persistent header Settings button. */
function GearIcon({ filled = false, size = 20 }) {
  return <SettingsIcon filled={filled} size={size} />;
}

const TabIcons = {
  phone: KeypadIcon,
  voicemail: VoicemailIcon,
  recents: RecentsIcon,
  contacts: ContactsIcon,
  messages: MessagesIcon,
  settings: SettingsIcon,
};

export { TabIcons, GearIcon };
export default TabIcons;
